import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import {
  ApplyCalendarLayoutAssignmentsOutDTO,
  CalendarLayoutAssignmentTargetInDTO,
  CalendarLayoutModelRowOutDTO,
} from '@api/ringil-api.v2';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ASSIGN } from './assignmentActions';
import { useAdminCompanyOptions } from './useAdminCompanyOptions';
import { lazyScrollerOptions, PICKER_PANEL_CLASS, truncatedValueTemplate, wrappingItemTemplate } from './pickerProps';
import { useCompanyLocationOptions } from './useCompanyLocationOptions';

interface ApplyModelDialogProps {
  /** The model being applied. Null closes the dialog. */
  model: CalendarLayoutModelRowOutDTO | null;
  admin: boolean;
  onHide: () => void;
  onApplied: (results: ApplyCalendarLayoutAssignmentsOutDTO) => void;
}

type Scope = 'company' | 'locations';

/**
 * Applies one model to companies and locations — the model-first direction of assignment.
 *
 * The assignments table answers "what does this location render"; this answers "where should this
 * model apply", which is what you want right after designing one. Both end up in the same
 * `applyAssignments` call.
 *
 * Admin picks the company; a customer's company is implicit (and the backend enforces it either
 * way — the picker is absent because it would only ever hold one entry, not as the security).
 */
export const ApplyModelDialog: React.FC<ApplyModelDialogProps> = ({ model, admin, onHide, onApplied }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { applyAssignments } = useContext(ApiContext);

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [scope, setScope] = useState<Scope>('company');
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [busy, setBusy] = useState(false);

  const open = model !== null;
  // Both pickers page as they are scrolled; typing searches server-side, so a company past the
  // loaded pages is still findable.
  const companies = useAdminCompanyOptions(open && admin);
  const locations = useCompanyLocationOptions(open, admin, companyId);
  // A customer only ever assigns within their own company, so it is fixed rather than chosen.
  const effectiveCompanyId = admin ? companyId : (loggedUser?.companyId ?? null);

  // Reset per opening: a dialog that remembers the previous model's targets is how you assign the
  // wrong layout to the wrong company.
  useEffect(() => {
    if (!open) return;
    setCompanyId(null);
    setLocationIds([]);
    setScope('company');
    setReplaceExisting(true);
  }, [open, model?.id]);

  const targets = (): CalendarLayoutAssignmentTargetInDTO[] => {
    if (!effectiveCompanyId || !model) return [];
    if (scope === 'company') return [{ calendarLayoutAction: ASSIGN, companyId: effectiveCompanyId, modelId: model.id }];
    return locationIds.map(locationId => ({ calendarLayoutAction: ASSIGN, companyId: effectiveCompanyId, locationId, modelId: model.id }));
  };

  const submit = () => {
    const list = targets();
    if (list.length === 0 || !model) return;
    setBusy(true);
    applyAssignments(
      { modelId: model.id, replaceExisting, targets: list },
      data => {
        setBusy(false);
        onApplied(data);
        onHide();
      },
      undefined,
      { onError: () => setBusy(false) },
    );
  };

  const canSubmit = !busy && !!effectiveCompanyId && (scope === 'company' || locationIds.length > 0);

  return (
    <RingilDialog
      visible={open}
      onHide={onHide}
      heading={tr('CalendarLayout.applyModel', 'Apply “{{name}}”', { name: model?.name ?? '' })}
      buttons={
        <>
          <RingilButton label={tr('CalendarLayout.apply', 'Apply')} mode="filled" disabled={!canSubmit} onClick={submit} />
          <RingilButton label={tr('CalendarLayout.cancel', 'Cancel')} mode="outlined" onClick={onHide} />
        </>
      }
    >
      <div className="flex flex-col gap-3 w-[520px]">
        {model && !model.published && (
          <div className="text-xs text-complementary-warning">
            {tr('CalendarLayout.applyUnpublished', 'This model has never been published — assigning it now renders nothing until you publish it.')}
          </div>
        )}

        {admin && (
          <Field label={tr('CalendarLayout.company', 'Company')} required={!companyId}>
            <Dropdown
              value={companyId}
              options={companies.options}
              onFilter={e => companies.search(e.filter)}
              virtualScrollerOptions={lazyScrollerOptions(companies.loading, companies.options.length, companies.hasMore, companies.loadMore)}
              onChange={e => {
                setCompanyId(e.value);
                // Locations belong to the company that was just replaced; keeping them selected
                // would silently assign to another company's locations.
                setLocationIds([]);
              }}
              filter
              placeholder={tr('CalendarLayout.chooseCompany', 'Choose a company')}
              className="w-full min-w-0"
              panelClassName={PICKER_PANEL_CLASS}
              itemTemplate={wrappingItemTemplate}
              valueTemplate={truncatedValueTemplate(tr('CalendarLayout.chooseCompany', 'Choose a company'))}
            />
          </Field>
        )}

        <Field label={tr('CalendarLayout.applyTo', 'Apply to')}>
          <div className="flex flex-row gap-2">
            <RingilButton
              label={tr('CalendarLayout.wholeCompany', 'Whole company')}
              mode={scope === 'company' ? 'filled' : 'outlined'}
              onClick={() => setScope('company')}
            />
            <RingilButton
              label={tr('CalendarLayout.specificLocations', 'Specific locations')}
              mode={scope === 'locations' ? 'filled' : 'outlined'}
              onClick={() => setScope('locations')}
            />
          </div>
        </Field>

        {scope === 'locations' && (
          <Field label={tr('CalendarLayout.locations', 'Locations')} required={locationIds.length === 0}>
            <MultiSelect
              value={locationIds}
              options={locations.options}
              onChange={e => setLocationIds(e.value)}
              virtualScrollerOptions={lazyScrollerOptions(locations.loading, locations.options.length, locations.hasMore, locations.loadMore)}
              filter
              display="chip"
              disabled={admin && !companyId}
              placeholder={
                admin && !companyId
                  ? tr('CalendarLayout.chooseCompanyFirst', 'Choose a company first')
                  : tr('CalendarLayout.chooseLocations', 'Choose locations')
              }
              className="w-full min-w-0"
              panelClassName={PICKER_PANEL_CLASS}
              itemTemplate={wrappingItemTemplate}
            />
          </Field>
        )}

        <div className="text-xs text-content-secondary">
          {scope === 'company'
            ? tr('CalendarLayout.companyScopeHint', 'Every location of the company renders this model, except those with a layout of their own.')
            : tr('CalendarLayout.locationScopeHint', 'Only the chosen locations render this model. It overrides any company-wide layout.')}
        </div>

        <label className="flex flex-row items-center gap-2 cursor-pointer text-sm">
          <Checkbox checked={replaceExisting} onChange={e => setReplaceExisting(!!e.checked)} />
          <span>{tr('CalendarLayout.replaceExisting', 'Replace layouts these targets already have')}</span>
        </label>
      </div>
    </RingilDialog>
  );
};
